import { Typography } from '@rhg/libs/material';
import { useCallback, useEffect, useRef, useState } from 'react';

export const ANNOUNCE_DELAY_MS = 50;

/**
 * The polite live region driven by {@link useAnnounce}.
 *
 * A stable component, so the region's node is mutated rather than replaced —
 * a region remounted with text already in it is not announced.
 *
 * Render this directly, with your own state, at sites that can't announce the
 * same string twice in a row and so don't need `useAnnounce`.
 */
export function AnnounceLiveRegion({ message }: { message: string }) {
    return (
        <Typography
            variant="srOnly"
            component="div"
            role="status"
            aria-live="polite"
            aria-atomic="true"
        >
            {message}
        </Typography>
    );
}

/**
 * An `announce()` that re-announces identical strings, by clearing the region
 * for a tick before each message. Render `<AnnounceLiveRegion message={message} />`
 * wherever the region belongs.
 */
export function useAnnounce() {
    const [message, setMessage] = useState('');
    const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

    const announce = useCallback((next: string) => {
        clearTimeout(timer.current);
        setMessage('');
        timer.current = setTimeout(() => setMessage(next), ANNOUNCE_DELAY_MS);
    }, []);

    useEffect(() => () => clearTimeout(timer.current), []);

    return { announce, message };
}
